<template>
  <div class="base-input-wrapper">
    <label v-if="label" :for="inputId" class="base-input__label">{{ label }}</label>
    <div class="base-input__container">
      <input
        :id="inputId"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        class="base-input"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
      <slot name="suffix"></slot>
    </div>
    <small v-if="hint" class="base-input__hint">{{ hint }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  modelValue: string | number;
  label?: string;
  type?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}>();

defineEmits<{
  'update:modelValue': [value: string];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const inputId = computed(() => `input-${Math.random().toString(36).substr(2, 9)}`);
</script>

<style scoped>
.base-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.base-input__label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.base-input__container {
  position: relative;
  display: flex;
  align-items: center;
}

.base-input--with-suffix {
  padding-right: 3rem;
}

.base-input__toggle-password {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  transition: color var(--transition-base);
}

.base-input__toggle-password:hover {
  color: var(--color-text-primary);
}

.base-input__toggle-password:focus {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.base-input {
  width: 100%;
  padding: 0.875rem var(--spacing-md);
  border: 2px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-family: var(--font-family);
  transition: all var(--transition-base);
  box-sizing: border-box;
}

.base-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: var(--shadow-focus);
}

.base-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--color-bg-tertiary);
}

.base-input__hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}
</style>

